//
// Este servicio encapsula toda la comunicación con la API de contenido de Ghost.
// Proporciona funciones para obtener una lista de publicaciones (con paginación
// y filtrado por etiquetas) y para recuperar una única publicación por su `slug`.
// Maneja los errores de manera que la aplicación pueda distinguir entre un
// post no encontrado y un error de red temporal.
//
// Módulo de conexión con Ghost CMS.
// Ghost corre de forma privada en blog.cagliero.ar y se usa únicamente como API de contenido.
// Este archivo es el único punto de contacto con Ghost en toda la app.
// Las credenciales se leen desde variables de entorno (.env) para no hardcodearlas en el código.

import GhostContentAPI from '@tryghost/content-api';

// Inicializa el cliente con las credenciales del entorno (o las públicas por defecto)
const api = new GhostContentAPI({
  url: process.env.REACT_APP_GHOST_URL || 'https://blog.cagliero.ar',
  key: process.env.REACT_APP_GHOST_KEY || '4bee1c95204ba69aa03b5d1198',
  version: 'v5.22',
});

// Trae un listado de posts filtrados por etiqueta con soporte de paginación.
// tagSlug: el slug de la etiqueta en Ghost (ej: 'datos', 'teatro', 'perfumeria').
// page: número de página (empieza en 1).
// limit: cantidad de posts por página.
export async function getGhostPosts({ tagSlug, page = 1, limit = 6 }) {
  try {
    const response = await api.posts.browse({
      filter: `tag:${tagSlug}`,
      include: 'tags,authors',
      limit,
      page,
    });
    return {
      posts: response,
      pagination: response.meta.pagination,
    };
  } catch (err) {
    console.error('Error al obtener posts de Ghost:', err);
    return { posts: [], pagination: {} };
  }
}

// Trae un único post por su slug para mostrarlo en la página de detalle (/post/:slug).
//
// Valores de retorno:
//   { ...post }         → post encontrado correctamente
//   { notFound: true }  → post definitivamente no existe (404 confirmado por Ghost)
//   null                → error de red o API temporalmente no disponible
//
// Distinguir estos casos es clave para el SEO: solo ponemos noindex cuando Ghost
// confirma que el post no existe (404), no cuando hay un error temporal de red
// (que ocurre cuando el crawler de Google llama a la API desde sus propios servidores).
export async function getSinglePost(postSlug) {
  try {
    return await api.posts.read({ slug: postSlug, include: 'tags,authors' });
  } catch (err) {
    // El SDK de Ghost lanza NotFoundError (statusCode 404) cuando el slug no existe.
    // Cualquier otro error (timeout, red, rate limit) se trata como fallo temporal.
    const isNotFound = err?.type === 'NotFoundError' || err?.statusCode === 404;
    if (isNotFound) {
      return { notFound: true };
    }
    console.error('Error al obtener el post individual:', err);
    return null; // null = error temporal, NO poner noindex
  }
}
